import { safeAwaitAll, SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import {
  ArticleSearchResponse,
  BreakingNewsResponse,
  NodeQueryParams,
  News,
  StoryObject,
  ChartbeatPost,
  ChartbeatParams,
  SearchResultResponse,
  SearchResultsParams,
} from './entities';
import { BasicNewsRestful, NodeContentRestful } from './restful';
import { Session } from '@benzinga/session';
import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';
import { BasicNewsStore } from './store';

interface BasicNewsRequestErrorEvent {
  error: SafeError;
  errorType:
    | 'news:simply_query_news_error'
    | 'news:fetch_nodes_error'
    | 'news:get_breaking_news_error'
    | 'news:get_latest_articles_error'
    | 'news:get_top_stories_error'
    | 'news:get_top_story_ids_error'
    | 'news:get_trending_india_stories_ids_error'
    | 'news:get_premium_articles_error'
    | 'article:get_article_error';
  type: 'error';
}

export type BasicNewsRestfulEvent = BasicNewsRequestErrorEvent;

export type NewsRequestSubscription = ReturnType<BasicNewsRequest['subscribe']>;

export class BasicNewsRequest extends ListenableSubscribable<BasicNewsRestfulEvent> {
  private restful: BasicNewsRestful;
  private restfulNodeContent: NodeContentRestful;
  private store: BasicNewsStore;

  constructor(session: Session, store?: BasicNewsStore) {
    super();
    this.restful = new BasicNewsRestful(session);
    this.restfulNodeContent = new NodeContentRestful(session);
    this.store = store ?? new BasicNewsStore();
  }

  public simplyQueryNews = async (
    query: SimpleNewsQuery = {},
    options: SimpleNewsOptions = {},
  ): SafePromise<StoryObject[]> => {
    const cached = this.store.get(query, options);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restful.simplyQueryNews(query, options);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:simply_query_news_error', type: 'error' });
      return { err: response.err };
    }
    if (response.ok) this.store.set(query, options, response.ok);
    return response;
  };

  public fetchNodes = async (query: NodeQueryParams = {}): SafePromise<News[]> => {
    const cached = this.store.getFetchNodes(query);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restfulNodeContent.fetchNodes(query);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:fetch_nodes_error', type: 'error' });
      return { err: response.err };
    }
    if (response.ok) this.store.setFetchNodes(query, response.ok);
    return response;
  };

  public getBreakingNews = async (): SafePromise<BreakingNewsResponse> => {
    const cached = this.store.getBreakingNews();
    if (cached !== undefined) return { ok: cached };
    const response = await this.restful.getBreakingNews();
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:get_breaking_news_error', type: 'error' });
      return response;
    }
    if (response.ok) this.store.setBreakingNews(response.ok);
    return response;
  };

  public getLatestArticles = async (query: string): SafePromise<ArticleSearchResponse> => {
    const cached = this.store.getLatestArticles(query);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restful.getLatestArticles(query);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:get_latest_articles_error', type: 'error' });
      return response;
    }
    if (response.ok) this.store.setLatestArticles(query, response.ok);
    return response;
  };

  public getStory = async (id: number): SafePromise<News> => {
    const cached = this.store.getStory(id);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restfulNodeContent.getStory(id);
    if (response.ok) this.store.setStory(id, response.ok);
    return response;
  };

  public getStoriesWithIds = async (ids: number[]): SafePromise<News[]> => {
    const reqs = ids.map(id => this.getStory(id));

    const response = await safeAwaitAll(reqs);

    if (response.err && response.err.length) {
      return { err: response.err[0] };
    }

    if (Array.isArray(response.ok)) {
      return {
        ok: response.ok
          .map(node => {
            return node?.[0];
          })
          .filter(node => node),
      };
    } else {
      return { ok: [] };
    }
  };

  public getSearchResults = async (params: SearchResultsParams): SafePromise<SearchResultResponse> => {
    const cached = this.store.getSearchResults(params);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restful.getSearchResults(params);
    if (response.ok) this.store.setSearchResults(params, response.ok);
    return response;
  };

  getTopPostsUsingChartbeat = async (params: ChartbeatParams = {}): SafePromise<{ pages: ChartbeatPost[] }> => {
    const cached = this.store.getChartbeat(params);
    if (cached !== undefined) return { ok: cached };
    const response = await this.restful.getTopPostsUsingChartbeat(params);
    if (response.ok) this.store.setChartbeat(params, response.ok);
    return response;
  };
}
